// S6.5 Phase 3 — PermissionCard.
//
// Single render for a permission_request message group. Inline in the chat
// stream, sticky-pinned below the chat header while pending. When the
// status flips terminal (allowed / denied / auto_allowed), the card
// collapses to a one-line PermissionOutcomePill.
//
// Founder-first contract (founder-first-vision.mdc §8 filter):
//   • Headline + body are humanized — never raw bash, JSON, or stack
//     traces. Raw input lives ONLY behind the ▽ "Show what Claude is
//     about to run" expander.
//   • Buttons: [Allow once] [Allow always for `<scope>`] [Deny].
//     Allow-always is hidden when the tool/input has no allowlistable
//     pattern (the backend would 400 the click anyway).
//   • Touch targets ≥ 44 px on mobile.
//   • Non-dismissable: no X button. Stop the agent from the composer to
//     escape (existing pattern).

import { useState } from 'react';
import { Icon } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import { humanizePermissionRequest } from '../../lib/permissions/humanize';
import { allowlistLabelFor } from '../../lib/permissions/allowlist-pattern';
import {
  isPermissionPending,
  type PermissionPendingContent,
  type PermissionTerminalContent,
} from '../../lib/permissions/types';
import { resolvePermission } from '../../lib/api';
import { useToastStore } from '../../stores/toast.store';
import { useAppStore } from '../../stores/app.store';
import { PermissionOutcomePill } from './PermissionOutcomePill';

export interface PermissionCardProps {
  agentId: string;
  pending: PermissionPendingContent;
  // Latest terminal content for this request_id, when present. The card
  // collapses to PermissionOutcomePill in that case.
  terminal?: PermissionTerminalContent;
  // Sticky behavior is opt-in: only the chronologically EARLIEST pending
  // card in a chat sticks. MessageList passes true for the first match,
  // false for the rest. Keeps multi-pending UX from stacking 3 sticky
  // cards on top of one another.
  sticky?: boolean;
}

export function PermissionCard({
  agentId,
  pending,
  terminal,
  sticky,
}: PermissionCardProps) {
  if (terminal) {
    // Terminal state — collapsed pill instead of the full card. Keeps the
    // chat scrollback compact (Santi often has 5-10 resolved cards above
    // an active turn).
    return (
      <PermissionOutcomePill
        toolName={pending.tool_name}
        toolInput={pending.tool_input}
        terminal={terminal}
      />
    );
  }
  return (
    <PendingCard
      agentId={agentId}
      pending={pending}
      sticky={sticky ?? false}
    />
  );
}

function PendingCard({
  agentId,
  pending,
  sticky,
}: {
  agentId: string;
  pending: PermissionPendingContent;
  sticky: boolean;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | 'allow_always' | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const pushToast = useToastStore((s) => s.push);
  const agentName = useAppStore((s) => s.agents[agentId]?.name ?? 'Agent');

  const humanized = humanizePermissionRequest({
    toolName: pending.tool_name,
    toolInput: pending.tool_input,
    classifierReason: pending.classifier_reason,
    agentName,
  });
  const allowlist = allowlistLabelFor(pending.tool_name, pending.tool_input);

  const submit = async (decision: 'allow' | 'deny' | 'allow_always') => {
    if (submitting) return;
    setSubmitting(decision);
    try {
      await resolvePermission(agentId, pending.request_id, { decision });
      // Success path: the websocket push for the terminal message will
      // re-render this same card slot as the OutcomePill. No optimistic
      // mutation needed.
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('permissions.couldNotSave');
      pushToast({
        message: msg.includes('REQUEST_GONE')
          ? t('permissions.requestGone')
          : t('permissions.couldNotSaveDetail', { error: msg }),
      });
      setSubmitting(null);
    }
  };

  const riskBorder =
    humanized.risk === 'high'
      ? 'border-l-status-error'
      : humanized.risk === 'medium'
        ? 'border-l-status-warning'
        : 'border-l-muted-foreground';
  const iconColor =
    humanized.risk === 'high'
      ? 'text-status-error'
      : humanized.risk === 'medium'
        ? 'text-status-warning'
        : 'text-muted-foreground';

  return (
    <div
      className={
        (sticky ? 'sticky top-0 z-[6] ' : '') +
        'rounded-md border border-border/50 bg-popover/95 backdrop-blur-md ' +
        `border-l-[3px] ${riskBorder} p-4 shadow-sm`
      }
      role="region"
      aria-label={t('permissions.askingPermission')}
      data-testid="permission-card"
      data-request-id={pending.request_id}
      data-risk={humanized.risk}
    >
      <div className="flex items-start gap-3">
        <Icon
          name="ShieldQuestion"
          className={`shrink-0 mt-0.5 ${iconColor}`}
          size={18}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div
            className="text-caption md:text-body font-semibold text-foreground leading-snug break-words"
            data-testid="permission-card-headline"
          >
            {humanized.headline}
          </div>
          {humanized.body && (
            <div
              className="mt-1 text-caption text-muted-foreground leading-snug break-words"
              data-testid="permission-card-body"
            >
              {humanized.body}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={submitting !== null}
          onClick={() => submit('allow')}
          className="min-h-touch-target px-4 rounded-md bg-action hover:bg-action/90 disabled:opacity-60 text-body font-medium text-action-foreground transition-colors flex items-center gap-2"
          data-testid="permission-card-allow"
        >
          {submitting === 'allow' && (
            <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
          )}
          {t('permissions.allowOnce')}
        </button>
        {allowlist.hasPattern && (
          <button
            type="button"
            disabled={submitting !== null}
            onClick={() => submit('allow_always')}
            className="min-h-touch-target px-4 rounded-md border border-border bg-card hover:bg-accent disabled:opacity-60 text-body font-medium text-foreground transition-colors flex items-center gap-2 max-w-full"
            data-testid="permission-card-allow-always"
            title={t('permissions.addsGlobalRule', { rule: allowlist.label })}
          >
            {submitting === 'allow_always' && (
              <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
            )}
            <span className="truncate">
              {t('permissions.allowAlwaysFor')} <code className="font-mono text-caption">{allowlist.label}</code>
            </span>
          </button>
        )}
        <button
          type="button"
          disabled={submitting !== null}
          onClick={() => submit('deny')}
          className="min-h-touch-target px-4 rounded-md border border-border/50 bg-transparent hover:bg-accent disabled:opacity-60 text-body font-medium text-status-error transition-colors flex items-center gap-2"
          data-testid="permission-card-deny"
        >
          {submitting === 'deny' && (
            <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
          )}
          {t('permissions.deny')}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setShowRaw((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-caption text-muted-foreground/45 hover:text-muted-foreground transition-colors min-h-[32px]"
        aria-expanded={showRaw}
        data-testid="permission-card-developer-toggle"
      >
        {showRaw ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronRight" size={12} />}
        {t('permissions.showRawDetail')}
      </button>
      {showRaw && (
        <pre
          className="mt-2 max-h-72 overflow-auto rounded bg-background border border-border/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap break-words" /* design-discipline: kept as text-xs — developer-mode raw detail */
          data-testid="permission-card-raw"
        >
          {`Tool: ${pending.tool_name}\n\n${formatInput(pending.tool_input)}`}
        </pre>
      )}
    </div>
  );
}

function formatInput(input: unknown): string {
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

// Re-export so MessageList only imports from this barrel-ish file. Keeps
// the pending check + this component co-located.
export { isPermissionPending };
